import { CalendarDays, ChevronLeft, ChevronRight, House } from "lucide-react";
import type { DashboardData, Owner, OwnerFilter } from "../types";
import { chromelessViews, type View } from "./views";
import Avatar from "../components/Avatar";
import { useProfileNames } from "../components/ProfilesContext";
import LoadingLabel from "../components/LoadingLabel";

// Each screen's heading, and the month and household-member controls beneath it, which only the
// screens that show money over time display.

type Heading = { eyebrow: string; title: string; blurb: string };
const SETTINGS_EYEBROW = "KEEP THE OPTIONS CLEAR";

/** What each screen calls itself. A Record, so a new screen cannot be added without its heading. */
const headings = (profile: Owner): Record<View, Heading> => ({
  overview: { eyebrow: `A LITTLE CHECK-IN, ${profile.toUpperCase()}`, title: "Home, in numbers.", blurb: "A clear view of what’s coming in and going out." },
  insights: { eyebrow: "A LITTLE PERSPECTIVE", title: "Your money, over time.", blurb: "Spot the patterns, see what changed, and follow the bigger picture." },
  accounts: { eyebrow: "EVERY POCKET, ONE PLACE", title: "Your accounts.", blurb: "A little here, a little there. All together." },
  transactions: { eyebrow: "THE EVERYDAY DETAILS", title: "The money trail.", blurb: "Every coffee, every bill, every little thing." },
  prices: { eyebrow: "THE THINGS YOU BUY", title: "Item prices.", blurb: "Compare brands, payees and prices over time." },
  more: { eyebrow: "PRICES, CATEGORIES & BUDGETS", title: "More.", blurb: "Prices, categories and budgets, all in one place." },
  settings: { eyebrow: SETTINGS_EYEBROW, title: "Settings.", blurb: "Profiles, AI, backups and errors at a glance." },
  "account-settings": { eyebrow: SETTINGS_EYEBROW, title: "Manage accounts.", blurb: "Create accounts and keep opening balances accurate." },
  "profile-settings": { eyebrow: SETTINGS_EYEBROW, title: "Profiles.", blurb: "Add people to the household and choose their pictures." },
  payees: { eyebrow: SETTINGS_EYEBROW, title: "Payees.", blurb: "Add and manage the people and businesses you deal with." },
  ious: { eyebrow: SETTINGS_EYEBROW, title: "IOUs.", blurb: "Track money you've lent or borrowed until it's settled." },
  bills: { eyebrow: SETTINGS_EYEBROW, title: "Recurring.", blurb: "Bills and regular income, due dates and history." },
  receipts: { eyebrow: "THE PAPER TRAIL", title: "Receipts.", blurb: "Every receipt photo you have saved, and the transaction it belongs to." },
  trips: { eyebrow: "ON THE ROAD", title: "Trips.", blurb: "Travel expenses, foreign currency wallets, and budgets." },
  goals: { eyebrow: "LOOKING AHEAD", title: "Savings goals.", blurb: "Earmark funds for targets, track monthly progress, and build wishlist pools." },
  categories: { eyebrow: SETTINGS_EYEBROW, title: "Categories.", blurb: "Manage categories and subcategories used for new entries." },
  budgets: { eyebrow: SETTINGS_EYEBROW, title: "Budgets.", blurb: "Set spending caps and track progress against them." },
  achievements: { eyebrow: SETTINGS_EYEBROW, title: "Achievements.", blurb: "Monthly wins and the goals still within reach." },
  ai: { eyebrow: SETTINGS_EYEBROW, title: "AI.", blurb: "Provider preference and today's shared allowance." },
  "smart-rules": { eyebrow: SETTINGS_EYEBROW, title: "Smart Rules.", blurb: "Card mapping, merchant categorization, and webhook configuration." },
  backups: { eyebrow: SETTINGS_EYEBROW, title: "Backups.", blurb: "Whether the daily ledger copy finished." },
  "staging-data": { eyebrow: SETTINGS_EYEBROW, title: "Staging tools.", blurb: "A small sample and a fresh start when you need it." },
  errors: { eyebrow: SETTINGS_EYEBROW, title: "Error logs.", blurb: "The last 100 recorded errors, newest first." },
});

export const viewHeading = (view: View, profile: Owner): Heading => headings(profile)[view];

export default function PageHeading({ view, profile, month, months, setMonth, changeMonth, owner, onOwnerChange, data }: {
  view: View;
  profile: Owner;
  month: string;
  months: string[];
  setMonth: (month: string) => void;
  changeMonth: (delta: number) => void;
  owner: OwnerFilter;
  onOwnerChange: (owner: OwnerFilter) => void;
  data: DashboardData | null;
}) {
  const profileNames = useProfileNames();
  const heading = viewHeading(view, profile);
  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">{heading.eyebrow}</p>
          <h1>{heading.title}</h1>
          <p className="muted">{heading.blurb}</p>
        </div>
        <div
          className="month-control"
          hidden={chromelessViews.includes(view)}
        >
          <button
            aria-label="Previous month"
            onClick={() => changeMonth(-1)}
          >
            <ChevronLeft size={16} />
          </button>
          <label>
            <CalendarDays size={15} />
            <select
              value={month}
              aria-label="Choose month"
              onChange={(e) => setMonth(e.target.value)}
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {new Date(m + "-01T12:00:00").toLocaleDateString(
                    "en-GB",
                    { month: "short", year: "numeric" },
                  )}
                </option>
              ))}
            </select>
          </label>
          <button aria-label="Next month" onClick={() => changeMonth(1)}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div className="view-controls">
        <div
          className="owner-tabs"
          hidden={chromelessViews.includes(view)}
          aria-label="Filter by owner"
        >
          {(["All", ...profileNames] as OwnerFilter[]).map((o) => (
            <button
              key={o}
              className={owner === o ? "selected" : ""}
              aria-pressed={owner === o}
              aria-label={o === "All" ? "Barnyard household" : o}
              title={o === "All" ? "Barnyard household" : o}
              onClick={() => onOwnerChange(o)}
            >
              {o === "All" ? (
                <House size={15} />
              ) : (
                <Avatar owner={o} size={21} />
              )}
              <span>{o === "All" ? "Our household" : o}</span>
            </button>
          ))}
        </div>
        <span className="updated">
          <span className="connection-dot" />
          {data
            ? `Updated ${new Date(data.fetchedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
            : <LoadingLabel>Connecting</LoadingLabel>}
        </span>
      </div>
    </>
  );
}
